import { useMemo } from "react";
import { Pie } from "@ant-design/plots";

type AnyProps = Record<string, any>;

const resolveRadius = (
  chartRadius: number | undefined,
  diameter: number,
  fallback: number
) => {
  if (typeof chartRadius !== "number" || chartRadius <= 0) {
    return fallback;
  }

  const normalized = chartRadius / (diameter / 2);
  return Math.max(0.1, Math.min(1, normalized));
};

export const PieChart = ({
  series = [],
  width = "100%",
  height = 350,
  className,
  slotProps,
}: AnyProps) => {
  const seriesConfig = series?.[0] ?? {};
  const data = useMemo(
    () =>
      (series?.[0]?.data ?? []).map((item: AnyProps, index: number) => ({
        key: `${item?.id ?? item?.label ?? "slice"}-${index}`,
        label: String(item?.label ?? ""),
        value: Number(item?.value ?? 0),
      })),
    [series]
  );

  const diameter =
    typeof height === "number" ? height : Number.parseInt(String(height), 10) || 350;
  const radius = resolveRadius(seriesConfig?.outerRadius, diameter, 0.82);
  const innerRadius = Math.min(
    radius - 0.05,
    resolveRadius(seriesConfig?.innerRadius, diameter, 0.45)
  );
  const hideLegend = Boolean(slotProps?.legend?.hidden);

  const config = useMemo(
    () => ({
      data,
      angleField: "value",
      colorField: "label",
      autoFit: true,
      height,
      radius,
      innerRadius,
      legend: hideLegend
        ? false
        : {
            position: "right",
          },
      label: hideLegend
        ? {
            type: "outer",
            formatter: (datum: AnyProps) =>
              `${datum.label}: ${(Number(datum.percent ?? 0) * 100).toFixed(1)}%`,
          }
        : false,
      tooltip: {
        formatter: (datum: AnyProps) => ({
          name: datum?.label ?? "",
          value: `${Number(datum?.value ?? 0).toFixed(2)} (${(
            Number(datum?.percent ?? 0) * 100
          ).toFixed(1)}%)`,
        }),
      },
      interactions: [{ type: "element-active" }],
      statistic: innerRadius > 0.2
        ? {
            title: false,
            content: {
              style: {
                whiteSpace: "pre-wrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#2f4f77",
                fontSize: "13px",
              },
              formatter: () => `Total\n${data.length}`,
            },
          }
        : undefined,
    }),
    [data, height, hideLegend, innerRadius, radius]
  );

  return (
    <div className={className} style={{ width, minHeight: height }}>
      <Pie {...(config as AnyProps)} />
    </div>
  );
};

export default PieChart;
